import { uniqBy } from 'lodash';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Subscription } from 'rxjs';

import { SocketContext } from '../providers/SocketProvider/SocketProvider';
import { ById, PartialParticipant } from '../services/backendService/types';
import { newRelicNoticeError } from './useNewRelic';

export const useGameParticipants = (gameId: string, tenantId: string) => {
  // This hook is used to get the participants of a game and subscribe to changes
  // It is a singleton, meaning that it will only subscribe once and the subscription will be kept
  // so dont use this hook more than one time on the same page
  const [participants, setParticipants] = useState<PartialParticipant[] | undefined>(
    undefined,
  );
  const [numberOfParticipants, setNumberOfParticipants] = useState<number>(0);

  const participantsRef = useRef<PartialParticipant[]>([]);
  const { gameAPI } = useContext(SocketContext);
  const intervalRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    let subChange: Subscription | undefined;
    let subCount: Subscription | undefined;

    if (gameId && gameAPI) {
      // unsubscribe from the other subscription

      const fetchParticipants = async ({ volatile }: { volatile: boolean }) => {
        const allParticipants = await gameAPI.game.listAllParticipants({
          gameId,
          tenantId,
          volatile,
        });
        if (!allParticipants) {
          setParticipants([]);
          participantsRef.current = [];
          setNumberOfParticipants(0);
          return;
        }
        setParticipants(allParticipants);
        setNumberOfParticipants(allParticipants.length);
        participantsRef.current = allParticipants;
      };

      fetchParticipants({ volatile: false });

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      intervalRef.current = setInterval(
        () => fetchParticipants({ volatile: true }),
        1000 * 60,
      );
      if (subChange) {
        subChange.unsubscribe();
      }
      subChange = gameAPI.game.listChangedParticipants({ gameId, tenantId }).subscribe({
        next: rawParticipants => {
          // updates the participants list with the new participants and the existing one
          if (!rawParticipants || rawParticipants.length === 0) {
            return;
          }

          // merges the rawParticipants into the existing participants list and avoid duplicates
          const newParticipants = uniqBy(
            [...rawParticipants, ...participantsRef.current],
            'id',
          );
          setParticipants(newParticipants);
          participantsRef.current = newParticipants;
        },
        error: error => {
          newRelicNoticeError(
            error,
            'Error while listening to game participants changes',
          );
        },
      });

      if (subCount) {
        subCount.unsubscribe();
      }
      subCount = gameAPI.game.countParticipants({ gameId, tenantId }).subscribe({
        next: nParticipants => {
          // updates the participants list with the new participants and the existing one
          if (nParticipants !== undefined) {
            setNumberOfParticipants(nParticipants);
          }
        },
        error: error => {
          newRelicNoticeError(error, 'Error while listening to game participants count');
        },
      });
    }

    return () => {
      if (subChange) {
        subChange.unsubscribe();
      }
      if (subCount) {
        subCount.unsubscribe();
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [gameAPI, gameId, tenantId]);

  const byId = useMemo(
    () =>
      (participants || []).reduce<ById<PartialParticipant>>(
        (acc, cur) => ({ ...acc, [cur.id]: cur }),
        {},
      ),
    [participants],
  );
  const participantsMemo = useMemo(() => (participants || []).filter(p => !p.isKicked), [
    participants,
  ]);
  return {
    allParticipants: participants || [],
    participants: participantsMemo,
    count: numberOfParticipants || 0,
    byId,
  };
};
